import React, { useState, useRef } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import DescriptionIcon from '@material-ui/icons/Description'
import { makeStyles } from '@material-ui/core/styles'
import axios from 'axios'

const useStyles = makeStyles((theme) => ({
  searchRow: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  resultCount: {
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary
  },
  selectedCount: {
    fontWeight: 600,
    color: theme.palette.primary.main
  },
  sourceChip: {
    marginLeft: theme.spacing(1),
    height: 20,
    fontSize: '0.7rem'
  },
  docsIcon: {
    color: theme.palette.success.main,
    fontSize: 16,
    verticalAlign: 'middle',
    marginLeft: 4
  }
}))

const SOURCE_LABELS = {
  vendor_catalog: 'Vendor Catalog',
  chemical: 'Chemicals',
  material_catalog: 'Material Catalog'
}

const SOURCE_CHIP_COLORS = {
  vendor_catalog: '#1976d2',
  chemical: '#388e3c',
  material_catalog: '#f57c00',
  ewing_online: '#00695c'
}

const getProductKey = (product) => {
  if (product._isLive) return `ewing-live:${product.urlKey || product.sku || product._id}`
  return `${product.sourceType}:${product._id}`
}

const getSourceLabel = (product) => {
  return product.sourceLabel || SOURCE_LABELS[product.sourceType] || product.sourceType
}

const getSourceColor = (product) => {
  if (product.sourceLabel === 'Ewing Online' || product.sourceKind === 'api' || product._isLive) {
    return SOURCE_CHIP_COLORS.ewing_online
  }
  return SOURCE_CHIP_COLORS[product.sourceType] || '#999'
}

const getDocsTooltip = (product) => {
  if (product._isLive || product.sourceKind === 'api') return 'Online product image and specifications available'
  if (product.sourceType === 'chemical') return `${product.documentCount || 0} document(s) (SDS/Label)`
  return 'Cutsheet page available'
}

const SubmittalProductPicker = ({ open, onClose, submittalId, onItemsAdded }) => {
  const classes = useStyles()
  const [query, setQuery] = useState('')
  const [sourceTab, setSourceTab] = useState(0) // 0=all, 1=vendor, 2=chemical, 3=material
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState(new Map()) // id -> result object
  const [adding, setAdding] = useState(false)
  const debounceRef = useRef(null)

  const sourceFilter = [null, 'vendor_catalog', 'chemical', 'material_catalog'][sourceTab]

  const search = async (q, source) => {
    if (!q.trim()) { setResults([]); return }
    setLoading(true)
    try {
      const params = { q, limit: 50 }
      if (source) params.source = source
      const { data } = await axios.get('/api/submittals/search', { params })
      setResults(data.results || [])
    } catch (err) {
      console.error('Search failed:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleQueryChange = (e) => {
    const val = e.target.value
    setQuery(val)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => search(val, sourceFilter), 300)
  }

  const handleSourceChange = (e, newTab) => {
    setSourceTab(newTab)
    const source = [null, 'vendor_catalog', 'chemical', 'material_catalog'][newTab]
    if (query.trim()) search(query, source)
  }

  const toggleSelect = (product) => {
    setSelected(prev => {
      const next = new Map(prev)
      const key = getProductKey(product)
      if (next.has(key)) next.delete(key)
      else next.set(key, product)
      return next
    })
  }

  const isSelected = (product) => {
    return selected.has(getProductKey(product))
  }

  const toggleSelectAll = () => {
    if (selected.size === results.length) {
      setSelected(new Map())
    } else {
      const next = new Map()
      results.forEach(r => next.set(getProductKey(r), r))
      setSelected(next)
    }
  }

  const hydrateLiveProduct = async (product) => {
    if (!product._isLive) return product
    if (!product.urlKey) throw new Error(`Missing Ewing urlKey for ${product.productName || product.sku || 'product'}`)

    const { data } = await axios.post('/api/vendor-catalogs/ewing/hydrate', { urlKey: product.urlKey })
    return {
      ...product,
      _isLive: false,
      _id: data._id,
      sourceId: data._id
    }
  }

  const handleAdd = async () => {
    if (selected.size === 0) return
    setAdding(true)
    try {
      const hydratedProducts = await Promise.all(Array.from(selected.values()).map(hydrateLiveProduct))
      const items = hydratedProducts.map(product => ({
        sourceType: product.sourceType,
        sourceId: product.sourceId || product._id
      }))
      await axios.post(`/api/submittals/${submittalId}/items`, { items })
      setSelected(new Map())
      setQuery('')
      setResults([])
      onItemsAdded()
    } catch (err) {
      console.error('Failed to add items:', err)
    } finally {
      setAdding(false)
    }
  }

  const handleClose = () => {
    setQuery('')
    setSourceTab(0)
    setResults([])
    setSelected(new Map())
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Add Products to Submittal</DialogTitle>
      <DialogContent>
        <Tabs
          value={sourceTab}
          onChange={handleSourceChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          style={{ marginBottom: 16 }}
        >
          <Tab label="All Sources" />
          <Tab label="Vendor Catalog" />
          <Tab label="Chemicals / Herbicides" />
          <Tab label="Material Catalog" />
        </Tabs>

        <div className={classes.searchRow}>
          <TextField
            placeholder="Search by name, SKU, active ingredient, or description..."
            value={query}
            onChange={handleQueryChange}
            fullWidth
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              )
            }}
          />
        </div>

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : results.length > 0 ? (
          <>
            <Typography variant="body2" className={classes.resultCount}>
              {results.length} results
              {selected.size > 0 && (
                <span className={classes.selectedCount}> — {selected.size} selected</span>
              )}
            </Typography>
            <TableContainer style={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={results.length > 0 && selected.size === results.length}
                        indeterminate={selected.size > 0 && selected.size < results.length}
                        onChange={toggleSelectAll}
                      />
                    </TableCell>
                    <TableCell>Product Name</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Manufacturer</TableCell>
                    <TableCell>SKU / Code</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Docs</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map(product => (
                    <TableRow
                      key={getProductKey(product)}
                      hover
                      onClick={() => toggleSelect(product)}
                      style={{ cursor: 'pointer' }}
                      selected={isSelected(product)}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox checked={isSelected(product)} />
                      </TableCell>
                      <TableCell>
                        <strong>{product.productName}</strong>
                        {product.description && (
                          <Typography variant="caption" display="block" color="textSecondary">
                            {product.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getSourceLabel(product)}
                          size="small"
                          style={{
                            backgroundColor: getSourceColor(product),
                            color: '#fff',
                            height: 22,
                            fontSize: '0.7rem'
                          }}
                        />
                      </TableCell>
                      <TableCell>{product.manufacturer || '—'}</TableCell>
                      <TableCell>{product.sku || '—'}</TableCell>
                      <TableCell>{product.category || '—'}</TableCell>
                      <TableCell>
                        {product.hasCutsheet && (
                          <Tooltip title={getDocsTooltip(product)}>
                            <DescriptionIcon className={classes.docsIcon} />
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : query.trim() ? (
          <Typography color="textSecondary" align="center" style={{ padding: 32 }}>
            No products found. Try a different search term or source tab.
          </Typography>
        ) : (
          <Typography color="textSecondary" align="center" style={{ padding: 32 }}>
            Search across vendor catalogs, chemicals, and your materials catalog.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleAdd}
          disabled={selected.size === 0 || adding}
        >
          {adding ? 'Adding...' : `Add ${selected.size} Item${selected.size !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SubmittalProductPicker
